/**
 * Lead qualification checklist service — thin wrapper over api/lead-qualification.js. Entirely
 * Clerk-authed. Backs the Decision-step checklist in CrmLeadDetailModal.jsx and the
 * `conversation_confirmed` override crmLeadNextStep.js reads — see docs/CRM.md's 2026-07
 * "Conversion criteria + pre-conversation screening" entries.
 */
async function clerkToken() {
  try { return typeof window.__clerkGetToken === 'function' ? await window.__clerkGetToken().catch(() => null) : null } catch { return null }
}

async function teamPost(body) {
  const token = await clerkToken()
  const res = await fetch('/api/lead-qualification', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Request failed')
  return res.json()
}

export const ANSWER_OPTS = ['yes', 'no', 'unsure']
export const ANSWER_LABELS = { yes: 'Yes', no: 'No', unsure: 'Unsure' }

export const QUALIFICATION_QUESTIONS = [
  { key: 'serious', label: 'Are they seriously considering moving forward?' },
  { key: 'decision_ready', label: 'Do they have a clear vision, ready to decide?' },
  { key: 'project_fit', label: 'Does the project fit what Alder James does well?' },
  { key: 'client_fit', label: 'Do they want a trusted guide, not to run every decision themselves?' },
  { key: 'resourced', label: 'Do they appear to have the resources to take this on in the near term?' },
]

/**
 * The qualifying-call reference. Internal-only — never read aloud verbatim, and never a
 * substitute for the paid Design Consultation's actual budget/reality-testing deliverable
 * (see brand/alder-james-brand-profile.md's "Fees" section) — this is a gut-check for the
 * team member on the call, not a quote given to the prospect. Grounded in the confidence-
 * focused register (brand profile, 2026-07 resolution): direct, assured, no hedging.
 */
export const CALL_GUIDE = {
  title: 'Qualifying call — what to walk away knowing',
  items: [
    {
      key: 'serious',
      q: 'Are they serious?',
      tip: 'Listen for a real timeline, whether they\'ve already spoken with other architects or contractors, and whether financing is in place. A vague timeline with no other signals usually means they\'re still early, not yet ready for this conversation.',
    },
    {
      key: 'decision_ready',
      q: 'Are they decision-ready?',
      tip: 'Which of our two tracks are they actually describing? "I want to explore the possibilities" is Design Consultation. "I already have a clear vision I want to execute" is Basic Services. Neither is wrong — it just tells you which track fits, and how ready they are to move.',
    },
    {
      key: 'project_fit',
      q: 'Is the project a fit?',
      tip: 'Our fee floor starts around $150K, running roughly 5-8% of construction cost. Does the scope described support that range? This is a gut-check for you, not a number to quote on the call — detailed budget/reality-testing is the paid Design Consultation\'s deliverable, not a free pre-screening step.',
    },
    {
      key: 'client_fit',
      q: 'Are they a fit as a client?',
      tip: 'Do they want a trusted guide, or do they want to control every decision themselves? Our best clients trust the process and want to focus on their life, not manage every choice. A client with money and a great project who insists on running every decision is still a mismatch.',
    },
    {
      key: 'resourced',
      q: 'Are they resourced to act now?',
      tip: 'Realism vs. dreaming: is there a real budget and timeline behind this, or is it still a "someday" idea? Listen for whether financing is actually in place, whether they own the property, and whether they\'ve set aside real time to work through it. A great project with no near-term ability to fund or pursue it isn\'t ready yet — that\'s not a fit issue, it\'s a timing one.',
    },
  ],
  closing: 'Not every project is a fit, and saying so is fine. "We\'re not sure this is the right fit for us — let\'s talk about why" is a legitimate, confident way to end this call.',
}

/** Returns the qualification-checklist row for a lead, or null if none exists yet. */
export async function getLeadQualification(leadId) {
  const res = await teamPost({ action: 'get', leadId })
  return (res && res.qualification) || null
}

/** Upserts a patch of qualification columns for a lead; returns the saved row. */
export async function upsertLeadQualification(leadId, patch) {
  const res = await teamPost({ action: 'upsert', leadId, patch })
  return (res && res.qualification) || null
}
